import { message } from 'antd';
import axios from 'axios';

export const AppToken = 'access_token';
export const AppRefreshToken = 'refresh_token';
export const BaseURL = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_SERVER_URL;
axios.defaults.baseURL = BaseURL;

let isRefreshing = false;
let failedQueue: { resolve: (value: unknown) => void; reject: (reason?: any) => void; }[] = [];
let isForcedLogoutHandled = false;

const processQueue = (error: unknown, token = null) => {
	failedQueue.forEach((prom) => {
		if (error) {
			prom.reject(error);
		} else {
			prom.resolve(token);
		}
	});

	failedQueue = [];
};

const refreshToken = async () => {
	const refreshToken = localStorage.getItem(AppRefreshToken);
	try {
		// Use a fresh axios instance to avoid interceptor loops
		const freshAxios = axios.create({ baseURL: BaseURL });
		const response = await freshAxios.post('/v1/auth/token/refresh/', { refresh: refreshToken });
		const newToken = response.data.access;
		localStorage.setItem(AppToken, newToken);
		localStorage.setItem(AppRefreshToken, response.data.refresh);

		return newToken;
	} catch (error) {
		throw error;
	}
};

axios.interceptors.request.use(
	(config) => {
		const token = localStorage.getItem(AppToken);
		// console.log('Request Interceptor - Token:', token);

		if (token) {
			config.headers.Authorization = 'Bearer ' + token;
		}
		return config;
	},
	(error) => {
		return Promise.reject(error);
	},
);

axios.interceptors.response.use(
	(response) => {
		if (response.data && response.data.redirect) {
			window.location.href = response.data.redirect;
		}
		return response.data;
	},
	async (error) => {
		const { config, response } = error;
		if (response?.status === 401 && response.data?.code === 'token_not_valid') {
			if (!response.data.messages) {
				window.location.href = '/login';
				localStorage.removeItem(AppToken);
				localStorage.removeItem(AppRefreshToken);
			}
			if (!isRefreshing) {
				isRefreshing = true;
				try {
					const newToken = await refreshToken();
					isRefreshing = false;
					processQueue(null, newToken);
					config.headers.Authorization = 'Bearer ' + newToken;
					return axios.request(config);
				} catch (err) {
					isRefreshing = false;
					processQueue(err, null);
					return Promise.reject(err);
				}
			}

			return new Promise((resolve, reject) => {
				failedQueue.push({ resolve, reject });
			})
				.then((token) => {
					config.headers.Authorization = 'Bearer ' + token;
					return axios.request(config);
				})
				.catch((err) => {
					return Promise.reject(err);
				});
		}
		if (response?.status === 401 && response.data?.code === 'user_not_found') {
			if (!response.data.messages) {
				window.location.href = '/login';
				localStorage.removeItem(AppToken);
				localStorage.removeItem(AppRefreshToken);
			}
		}
		if (response?.status === 401 && response.data?.code === 'forced_logout') {
			// Any password change (self or admin-triggered) invalidates every
			// session for that account, including the one that made the change -
			// there's no "still valid, just rotated" token to retry with.
			localStorage.removeItem(AppToken);
			localStorage.removeItem(AppRefreshToken);
			// Several requests can fail with this at once (parallel API calls,
			// retried queue) — only show the toast and redirect once.
			if (!isForcedLogoutHandled) {
				isForcedLogoutHandled = true;
				message.error('Your password was reset by an admin. Please log in with your new password.', 4);
				// Give the message time to render before the page navigates away
				setTimeout(() => {
					window.location.href = '/login';
				}, 1500);
			}
			return Promise.reject(error);
		}
		return errorMsgHandler(error);
	},
);

let isUnauthorizedMessageShown = false;
let isServerErrorMessageShown = false;

export const errorMsgHandler = (error: any) => {
	const showGlobalError = !error.config?.suppressGlobalErrorMessage;
	switch (error.response?.status) {
		case 400:
			if (showGlobalError && error.response.data?.non_field_errors) {
				message.error(error.response.data.non_field_errors);
			}
			if (showGlobalError && error.response.data?.detail) {
				message.error(error.response.data.detail);
			}
			if (showGlobalError && error.response.data?.message) {
				message.error(error.response.data.message);
			}
			const validationErrors: { name: string; errors: any; }[] = [];
			Object.keys(error?.response?.data).forEach((field) => {
				validationErrors.push({ name: field, errors: error.response.data[field] });
			});
			return Promise.resolve({ success: false, validation_errors: validationErrors });

		case 401:
			const errorMessage = error.response?.data?.detail || 'Unauthorized Access';
			if (errorMessage.includes('Your account is blocked')) {
				message.error(errorMessage);
			} else if (!isUnauthorizedMessageShown) {
				isUnauthorizedMessageShown = true;
				message.error(errorMessage);
				setTimeout(() => {
					isUnauthorizedMessageShown = false;
				}, 2000); // Reset after 2 seconds
			}
			return Promise.reject();

		case 404:
			message.error(error.response?.data?.message || error.response?.data.detail || 'Request URL not found.');
			return Promise.reject();

		case 408:
			message.error(error.response?.data?.message);
			return Promise.reject();

		case 500:
			if (!isServerErrorMessageShown) {
				isServerErrorMessageShown = true;
				message.error(error.response.statusText);
				setTimeout(() => {
					isServerErrorMessageShown = false;
				}, 2000); // Reset after 2 seconds
			}
			return Promise.reject();

		default:
			if (showGlobalError) {
				const detail = error.response?.data?.detail || error.response?.data?.message || error.response?.statusText;
				if (detail) message.error(detail);
			}
			return Promise.reject(error);
	}
};

/**
 * Extract a human-readable error message from an API error response.
 * Handles both raw axios errors (thrown) and interceptor-wrapped responses
 * ({ success: false, validation_errors: [...] }).
 */
export const getApiErrorMessage = (error: unknown, fallback = 'Request failed'): string => {
	// Try interceptor-wrapped format first (resolved promise with validation_errors)
	const wrapped = error as { success?: boolean; validation_errors?: { name: string; errors: string | string[] }[] };
	if (wrapped?.validation_errors) {
		const detail = wrapped.validation_errors.find((e) => e.name === 'detail')?.errors;
		if (detail) return Array.isArray(detail) ? detail.join(', ') : detail;
		const first = wrapped.validation_errors[0]?.errors;
		if (first) return Array.isArray(first) ? first.join(', ') : first;
	}

	// Try raw axios error format (rejected promise with response.data)
	const axiosErr = error as { response?: { data?: Record<string, unknown> } };
	const data = axiosErr?.response?.data;
	if (data) {
		if (typeof data.detail === 'string') return data.detail;
		if (typeof data.message === 'string') return data.message;
		const fieldErrors = Object.entries(data)
			.filter(([_, v]) => Array.isArray(v))
			.map(([k, v]) => `${k}: ${(v as string[]).join(', ')}`);
		if (fieldErrors.length) return fieldErrors.join('; ');
	}

	if (error instanceof Error) return error.message;

	return fallback;
};

export default axios;
